/* eslint-disable no-unused-vars */
import { ITicker, ITickersSort } from '@kattana-io/common';
import { IRates } from '../../../reducers/rates';
import { formatNumber } from '../../DexTrades/formatters';

export interface IColumn {
  name: ColumnNames;
  title?: ColumnTitles;
  filterable?: boolean;
}

export enum ColumnNames {
  pair = 'pair',
  price = 'price_a_usd',
  liquidity = 'liquidity',
  volume = 'volume',
  exchange = 'exchange',
  star = 'star',
  contract = 'contract',
  apy = 'annual_percentage_yield',
}

export enum ColumnTitles {
  pair = 'Pairs',
  price = 'Price',
  liquidity = 'Liquidity',
  volume = 'Volume',
  contract = 'Contract',
  apy = 'APY',
}

/**
 * Constants
 */
const precision = 4; // 3.9312

const enableLiquiditFilter = (chain: string) => {
  return ![
    'BSC_TESTNET',
    'ARTHERA_TESTNET',
    'ARBITRUM_SEPOLIA_TESTNET',
    'OKX',
    'CRONOS',
    'AURORA',
    'METER',
    'KAVA',
    'MOONBEAM',
    'HARMONY',
    'KLAYTN',
    'GNOSIS',
    'OPTIMISM',
  ].includes(chain);
};

const formatLiquidity = (liq: number) => {
  try {
    if (liq) {
      return formatUSD(Math.round(liq));
    }
    return '';
  } catch (err) {
    console.warn('Unable to format liquidity', err);
    return '';
  }
};

const usdFormatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const formatUSD = (liq: number) => usdFormatter.format(liq); /* $2,500.00 */

const formatPriceUSD = (price: any) =>
  '$' + parseFloat(price).toFixed(precision);

const formatPrice = (price: any) => parseFloat(price).toFixed(precision);

const formatBaseVolume = (volume: number): string => {
  if (volume) {
    return '$' + formatNumber(Math.round(volume));
  }
  return '';
};

const getPriceAgainstQuote = (price: number, quote: string, rates: any) => {
  if (['WETH', 'ETH'].includes(quote)) {
    return {
      rawPrice: price * parseFloat(rates.ETH),
      formattedPrice: formatPriceUSD(price * parseFloat(rates.ETH)),
      sign: '$',
    };
  }
  if (['WBTC', 'BTC'].includes(quote)) {
    return {
      rawPrice: price * parseFloat(rates.BTC),
      formattedPrice: formatPriceUSD(price * parseFloat(rates.BTC)),
      sign: '$',
    };
  }
  if (['WMATIC', 'MATIC'].includes(quote)) {
    return {
      rawPrice: price * parseFloat(rates.MATIC),
      formattedPrice: formatPriceUSD(price * parseFloat(rates.MATIC)),
      sign: '$',
    };
  }
  if (quote.includes('USD')) {
    return {
      rawPrice: price,
      formattedPrice: formatPriceUSD(price),
      sign: '$',
    };
  }
  if (rates[quote]) {
    return {
      rawPrice: price * parseFloat(rates[quote]),
      formattedPrice: formatPriceUSD(price * parseFloat(rates[quote])),
      sign: '$',
    };
  }
  // 1234 UAH
  return {
    rawPrice: price,
    formattedPrice: `${formatPrice(price)} ${quote}`,
    sign: '',
  };
};

const getPrice = (item: ITicker, rates: IRates) => {
  /**
   * Return formatted usd price if we have it
   */
  const price_usd = item.flipped ? item.price_b_usd : item.price_a_usd;
  if (price_usd) {
    return {
      rawPrice: price_usd,
      formattedPrice: formatPriceUSD(price_usd),
      sign: '$',
    };
  }
  /**
   * Try to convert using quotes for CEX
   * because we can't know if quote is legit for DEX
   */
  if (item.chain == '') {
    const [base, quote] = item.name.split('/');
    const price = item.flipped ? item.price_b : item.price_a;
    const converted_usd = getPriceAgainstQuote(price, quote, rates);
    if (converted_usd) {
      return converted_usd;
    }
    // Maybe we have raw rate in rates object for base?
    if (rates[base]) {
      return {
        rawPrice: rates[base],
        formattedPrice: formatPriceUSD(rates[base]),
        sign: '$',
      };
    }
  }

  return {
    rawPrice: 0,
    formattedPrice: '',
    sign: '',
  };
};

const liquidityDescTickersSort: ITickersSort = {
  name: 'liquidity',
  order: 'DESC',
};

const volumeDescTickersSort: ITickersSort = { name: 'volume', order: 'DESC' };

/**
 * Uppercase first letter
 */
const capitalize = (s: string) => {
  if (typeof s !== 'string') {
    return '';
  }
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export {
  formatBaseVolume,
  formatLiquidity,
  formatPrice,
  formatPriceUSD,
  getPrice,
  capitalize,
  liquidityDescTickersSort,
  volumeDescTickersSort,
  formatUSD,
  enableLiquiditFilter,
};
